import classNames from 'classnames';
import type { FC } from 'react';
import { Fragment, memo } from 'react';

import { useBlockedUsers } from '../../../../graphdebate/src/hooks/useBlockedUsers';
import { useHasAds } from '../../../../graphdebate/src/utils/common';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useAppSelector } from '../../redux/hooks';
import { selectCustomDensity } from '../../redux/slices/utils-slice';
import type { Topic } from '../../types/api';
import type { DensityType } from '../../utils/density';
import { TopicListItemCard } from './children/density/TopicListItemCard';
import { TopicLd } from './TopicLd';
import { TopicListItemV3 } from './TopicListItemV3';
import './TopicListStyles.scss';

export interface ComputedAd {
  position: number;
  element: JSX.Element | null;
}

interface Props {
  topics: Topic[];
  density?: DensityType;
  computedAds?: ComputedAd[];
  withRemove?: boolean;
  withActions?: boolean;
  topBorder?: boolean;
  bottomBorder?: boolean;
  isResponsive?: boolean;
  isSidebar?: boolean;
  itemTitleClassName?: string;
  isRefreshingData?: boolean;
  shouldHandleDsaDisplay?: boolean;
}

export const TopicList: FC<Props> = memo(function TopicList(props) {
  let {
    topics,
    density,
    computedAds,
    withRemove,
    withActions,
    topBorder,
    bottomBorder,
    isResponsive = true,
    isSidebar = false,
    itemTitleClassName = '',
    shouldHandleDsaDisplay = false,
  } = props;
  const hasAds = useHasAds();
  const isMobile = useIsMobile();
  const { isBlocked } = useBlockedUsers();

  if (!hasAds) {
    computedAds = undefined;
  }

  const defaultDensity = (isMobile ? 'compact' : 'standard') as DensityType;
  const customDensity = useAppSelector(selectCustomDensity);
  const currentDensity = density || customDensity || defaultDensity;

  const ListItem =
    isSidebar || currentDensity !== 'card' || shouldHandleDsaDisplay
      ? TopicListItemV3
      : TopicListItemCard;

  return (
    <div
      className={classNames(
        'TopicList *:bg-white',
        !shouldHandleDsaDisplay && 'border border-gray-210 rounded-[5px]',
        topBorder && 'border-t',
        bottomBorder && 'border-b',
        isSidebar && 'border-l-0 border-r-0 pl-1',
        (topBorder || bottomBorder) && 'border-gray-210',
      )}
    >
      {topics.map((topic, index) => {
        const positionAds = computedAds?.filter(elt => elt?.element && elt.position === index);
        let ads = positionAds && positionAds.length > 0 ? positionAds.map(ad => ad.element) : null;
        const isAuthorBlocked = isBlocked((topic.author as any)?.id);
        return (
          <Fragment key={topic?.id}>
            {!isSidebar && <TopicLd topic={topic as Topic} />}
            {ads}
            <ListItem
              topic={topic as Topic}
              isAuthorBlocked={isAuthorBlocked}
              density={currentDensity}
              isResponsive={isResponsive}
              isSidebar={isSidebar}
              withRemove={withRemove}
              withActions={withActions}
              itemTitleClassName={itemTitleClassName}
              shouldHandleDsaDisplay={shouldHandleDsaDisplay}
            />
          </Fragment>
        );
      })}
    </div>
  );
});
